import {
	BusyIndicator,
	Button,
	Checkbox,
	ConfirmationDialog,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Portal,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Tabs,
	Toolbar,
	ValidationError
} from "client/Components";
import { Browsers, createBrowsersValidation } from "client/Features/ConditionalAccess/Browsers";
import { createDevicesValidation, Devices } from "client/Features/ConditionalAccess/Devices";
import { createIpAddressesValidation, IpAddresses } from "client/Features/ConditionalAccess/IpAddresses";
import { createOperatingSystemsValidation, OperatingSystems } from "client/Features/ConditionalAccess/OperatingSystems";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { attachToUserAndUserGroupCollection } from "client/Features/Users/UserAndUserGroupCollection/attachable";
import { UserAndUserGroupCollection, UserAndUserGroupKind } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog
} from "client/Hooks";
import { setClientId } from "client/Misc/clientId";
import { isNotNil } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	EditSharedGroupModel,
	forceDefaultVisibility,
	getSharedTileGroupData,
	saveSharedTileGroupSettings,
	SaveSharedTileGroupSettingsModel,
	SaveTileGroupConditionalAccessSettingsModel
} from "client/Api/Workspace/workspace";
import styles from "./EditSharedTileGroup.module.less";

const validationBuilder: IValidationConfiguration<SaveSharedTileGroupSettingsModel> = createValidationConfiguration<SaveSharedTileGroupSettingsModel>();

const conditionalAccessValidation: IObjectValidatorBuilder<SaveTileGroupConditionalAccessSettingsModel> = validationBuilder
	.forObject("conditionalAccessSettings");

conditionalAccessValidation.addValidators(
	createDevicesValidation<SaveTileGroupConditionalAccessSettingsModel>(),
	createOperatingSystemsValidation<SaveTileGroupConditionalAccessSettingsModel>(),
	createBrowsersValidation<SaveTileGroupConditionalAccessSettingsModel>(),
	createIpAddressesValidation<SaveTileGroupConditionalAccessSettingsModel>()
);

export type EditSharedTileGroupProps = {
	id: number;
	isInPersonalSpace: boolean;
	onSave: (id: number, isHiddenByDefaultOnPersonalSpace: boolean) => void;
	onClose: () => void;
};

export const EditSharedTileGroup = (props: EditSharedTileGroupProps): ReactElement => {
	const {
		id,
		isInPersonalSpace,
		onSave,
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [tileGroupName, setTileGroupName] = useState<string>();
	const [userAndUserGroupData, setUserAndUserGroupData] = useState<UsersAndUserGroupsDataModel>();
	const [formData, setFormData] = useState<SaveSharedTileGroupSettingsModel>(
		getNewSaveModel
	);

	const [isShowingForceDefeaultVisibility, setIsShowingForceDefeaultVisibility] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<SaveSharedTileGroupSettingsModel> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	useEffect(
		(): (() => void) => {
			const load = async (id: number, signal: AbortSignal): Promise<void> => {
				try {
					const data: EditSharedGroupModel = await getSharedTileGroupData(id, signal);

					setFormData(mapToSaveModel(data));

					setTileGroupName(data.name);
					setUserAndUserGroupData(data.usersAndUserGroupsData);

					setIsLoading(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			id,
			onClose
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			try {
				if (isSaving) {
					return;
				}

				setIsSaving(true);

				const validationResult: InputValidationResult = await formHandler.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsSaving(false);
					return;
				}

				const model: SaveSharedTileGroupSettingsModel = prepareModelForSaving(formData);

				await saveSharedTileGroupSettings(model);

				onSave(id, model.isHiddenByDefaultOnPersonalSpace);

				setIsSaving(false);
				onClose();
			} catch (e) {
				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsSaving(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			id,
			isSaving,
			onClose,
			onSave
		]
	);

	const confirmForceDefaultVisibilityHandler = useCallback(
		(): void => {
			setIsShowingForceDefeaultVisibility(false);

			forceDefaultVisibility(id)
				.catch(() => alertManager.showError(localize("_GeneralErrorMessage")));
		},
		[alertManager, id]
	);

	if (isLoading) {
		return (
			<Portal container={document.body}>
				<BusyIndicator />
			</Portal>
		);
	}

	return (
		<>
			<Dialog
				trackingId="edit-shared-tile-group"
				hasBusyIndicator={isSaving}
				variant="left-slider"
				onClose={onClose}
			>
				<Dialog.Header
					title={
						<FormattedText
							text={localize("_ManageName")}
							thingsToReplace={[
								{ what: "{{Name}}", filler: tileGroupName}
							]}
						/>
					}
					onClose={onClose}
					hasBorder
				/>

				<Dialog.Body>
					<Form onSubmit="NONE" height="fill">
						<Tabs height="fill">
							<Tabs.Tab
								{...formHandler.under("whoHasAccess").attachToTab()}
								id="who-has-access"
								title={localize("_WhoHasAccess")}
							>
								<div className={styles.userAndUserGroups}>
									<FormItem>
										<Checkbox
											{...formHandler.for("everyoneHasAccess").attachToCheckbox()}
											text={localize("_EveryoneHasAccess")}
										/>
										<ValidationError {...formHandler.for("everyoneHasAccess").attachToValidationError()} />
									</FormItem>
									{isNotNil(userAndUserGroupData) && !formData.everyoneHasAccess && (
										<UserAndUserGroupCollection
											activeTab={ActiveTab.UserGroup}
											{...attachToUserAndUserGroupCollection(
												formHandler.for("whoHasAccess"),
												{
													kind: UserAndUserGroupKind.Edit,
													dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
												}
											)}
											allUsersAndUserGroupsData={userAndUserGroupData}
										/>
									)}
								</div>
							</Tabs.Tab>

							<Tabs.Tab
								{...formHandler.under("owners").attachToTab()}
								id="owners"
								title={localize("_Owners")}
							>
								<div className={styles.userAndUserGroups}>
									{isNotNil(userAndUserGroupData) && (
										<UserAndUserGroupCollection
											activeTab={ActiveTab.UserGroup}
											{...attachToUserAndUserGroupCollection(
												formHandler.for("owners"),
												{
													kind: UserAndUserGroupKind.Edit,
													dialogTitleWhenAddingUsersAndUserGroups: localize("_AddOwners")
												}
											)}
											allUsersAndUserGroupsData={userAndUserGroupData}
										/>
									)}
								</div>
							</Tabs.Tab>

							{isInPersonalSpace && (
								<Tabs.Tab
									id="visibility"
									title={localize("_Visibility")}
								>
									<FormItem
										label={localize("_DefaultVisibilityToUsersOnPersonalSpace")}
										isForGroup
									>
										<RadioButtonGroup
											{...formHandler.for("isHiddenByDefaultOnPersonalSpace")
												.attachToRadioButtonGroup()}
											options={visibilityOptions}
										/>
									</FormItem>

									<FormItem>
										<div>
											<Button
												kind="primary"
												text={localize("_ClearUserVisibilityPreference")}
												onClick={() => setIsShowingForceDefeaultVisibility(true)}
											/>
										</div>
									</FormItem>
								</Tabs.Tab>
							)}

							<Tabs.Tab
								{...formHandler.under("conditionalAccessSettings").attachToTab()}
								id="conditional-access"
								title={localize("_ConditionalAccess")}
							>
								<h2 className="title">{localize("_Devices")}</h2>
								<FormItem
									label={localize("_SupportedOn")}
									isForGroup
								>
									<Devices
										attachable={formHandler.for("conditionalAccessSettings")}
									/>
								</FormItem>

								<h2 className="title">{localize("_OperatingSystems")}</h2>
								<FormItem
									label={localize("_SupportedOn")}
									isForGroup
								>
									<OperatingSystems
										attachable={formHandler.for("conditionalAccessSettings")}
									/>
								</FormItem>

								<h2 className="title">{localize("_Browsers")}</h2>
								<FormItem
									label={localize("_SupportedOn")}
									isForGroup
								>
									<Browsers
										attachable={formHandler.for("conditionalAccessSettings")}
									/>
								</FormItem>

								<h2 className="title">{localize("_IPRanges")}</h2>
								<FormItem
									label={localize("_SupportedOn")}
									isForGroup
								>
									<IpAddresses
										attachable={formHandler.for("conditionalAccessSettings")}
									/>
								</FormItem>
							</Tabs.Tab>

						</Tabs>
					</Form>
				</Dialog.Body>

				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							trackingId="save"
							kind="primary"
							text={localize("_Save")}
							onClick={saveHandler}
						/>

						<Toolbar.Button
							trackingId="cancel"
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{isShowingForceDefeaultVisibility && (
				<ConfirmationDialog
					title={localize("_AreYouSureYouWantToClearUsersVisibilityPreferenceOfThisTileGroup")}
					confirmText={localize("_ClearUserVisibilityPreference")}
					onConfirmation={confirmForceDefaultVisibilityHandler}
					onCancellation={() => setIsShowingForceDefeaultVisibility(false)}
					trackingId="clear-tile-group-visibility-preference-confirmation-dialog"
				>
					{localize("_ClearUserVisibilityPreferenceForTileGroupMessage")}
				</ConfirmationDialog>
			)}
		</>
	);
};
EditSharedTileGroup.displayName = "EditSharedTileGroup";

export type EditSharedTileGroupInitialData = Omit<EditSharedTileGroupProps, "onClose" | "onSave">;
export type EditSharedTileGroupResultData = {
	id: number;
	isHiddenByDefaultOnPersonalSpace: boolean;
};

export function useEditSharedTileGroup(): [
	element: ReactElement | null,
	open: (props: EditSharedTileGroupInitialData) => Promise<TryResult<EditSharedTileGroupResultData>>
] {
	return useDataInputDialog<EditSharedTileGroupInitialData, EditSharedTileGroupResultData>(
		(data, onClose, onSave) => (
			<EditSharedTileGroup
				{...data}
				onClose={onClose}
				onSave={(id, isHiddenByDefaultOnPersonalSpace) => onSave({id, isHiddenByDefaultOnPersonalSpace})}
			/>
		)
	);
}

const visibilityOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_ShowGroup"),
		value: false
	},
	{
		text: localize("_HideGroup"),
		value: true
	}
];

function getNewSaveModel(): SaveSharedTileGroupSettingsModel {
	return {
		tileGroupId: -1,
		whoHasAccess: {
			userIds: [],
			userGroupIds: []
		},
		owners: {
			userIds: [],
			userGroupIds: []
		},
		isHiddenByDefaultOnPersonalSpace: false,
		conditionalAccessSettings: {
			restrictDevices: false,
			devices: [],
			restrictOperatingSystems: false,
			operatingSystems: [],
			restrictBrowsers: false,
			browsers: [],
			restrictIp: false,
			whitelistIpRanges: [],
			blacklistIpRanges: []
		},
		everyoneHasAccess: false
	};
};

function mapToSaveModel(data: EditSharedGroupModel): SaveSharedTileGroupSettingsModel {
	const {
		id,
		whoHasAccess,
		owners,
		isHiddenByDefaultOnPersonalSpace,
		conditionalAccessSettings,
		everyoneHasAccess
	} = data;

	return {
		tileGroupId: id,
		whoHasAccess,
		owners,
		isHiddenByDefaultOnPersonalSpace,
		conditionalAccessSettings: {
			...conditionalAccessSettings,
			whitelistIpRanges: conditionalAccessSettings.whitelistIpRanges.map(x => setClientId(x)),
			blacklistIpRanges: conditionalAccessSettings.blacklistIpRanges.map(x => setClientId(x))
		},
		everyoneHasAccess
	};
}

function prepareModelForSaving(model: SaveSharedTileGroupSettingsModel): SaveSharedTileGroupSettingsModel {
	const {
		conditionalAccessSettings,
		everyoneHasAccess,
		whoHasAccess
	} = model;

	if (!conditionalAccessSettings.restrictDevices) {
		conditionalAccessSettings.devices = [];
	}

	if (!conditionalAccessSettings.restrictOperatingSystems) {
		conditionalAccessSettings.operatingSystems = [];
	}

	if (!conditionalAccessSettings.restrictBrowsers) {
		conditionalAccessSettings.browsers = [];
	}

	if (!conditionalAccessSettings.restrictIp) {
		conditionalAccessSettings.whitelistIpRanges = [];
		conditionalAccessSettings.blacklistIpRanges = [];
	}

	return {
		...model,
		conditionalAccessSettings,
		whoHasAccess: everyoneHasAccess
			? null
			: whoHasAccess
	};
}